import React from "react";
import { FiInfo, FiCoffee } from "react-icons/fi";
import MonthlySalesChart from "../../../../tailadmin/components/ecommerce/MonthlySalesChart";
import PieChart from "../../../../tailadmin/components/ecommerce/PieChart";
import { FoodLogStats } from "./types";
import { MEAL_TYPE_COLORS, WEEKDAY_COLORS } from "./constants";

interface ChartsSectionProps {
  stats: FoodLogStats;
}

export default function ChartsSection({ stats }: ChartsSectionProps) {
  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
    });
  };

  const caloriesCategories = stats.frequency_by_day.map((d) =>
    formatDate(d.date),
  );
  const caloriesData = stats.frequency_by_day.map((d) => d.calories);

  const mealTypeLabels = stats.meal_type_stats.map((m) => m.meal_name);
  const mealTypeData = stats.meal_type_stats.map((m) => m.count);
  const mealTypeColorsArray = mealTypeLabels.map(
    (label) => MEAL_TYPE_COLORS[label] || "#9333ea",
  );

  const weekdayLabels = stats.weekday_stats.map((w) => w.day);
  const weekdayCaloriesData = stats.weekday_stats.map((w) => w.calories);
  const weekdayColorsArray = weekdayLabels.map(
    (label) => WEEKDAY_COLORS[label] || "#9333ea",
  );

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      {/* Consumo Calórico Diário e Nuvem de Palavras - lado a lado */}
      {(stats.frequency_by_day.length > 0 || stats.word_cloud_image_url) && (
        <>
          {stats.frequency_by_day.length > 0 && (
            <div className="col-span-1 h-[300px] sm:h-[350px] lg:h-[400px]">
              <MonthlySalesChart
                title="Consumo Calórico Diário"
                data={caloriesData}
                categories={caloriesCategories}
                tooltipText="Este gráfico mostra o consumo diário de calorias do aluno ao longo do período selecionado. As barras representam as calorias consumidas em cada dia, e a linha roxa indica a média diária de calorias no período."
              />
            </div>
          )}
          <div
            className={`col-span-1 h-[300px] sm:h-[350px] lg:h-[400px] ${
              stats.frequency_by_day.length === 0 ? "lg:col-span-2" : ""
            }`}
          >
            <div className="flex h-full flex-col rounded-xl sm:rounded-2xl border border-stroke bg-white shadow-sm dark:border-strokedark dark:bg-boxdark">
              <div className="border-b border-stroke px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4 dark:border-strokedark">
                <div className="flex items-center gap-2">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-800 dark:text-white/90">
                    Alimentos Mais Consumidos
                  </h3>
                  <div className="group relative">
                    <FiInfo
                      size={16}
                      className="sm:w-[18px] sm:h-[18px] cursor-help text-gray-400 transition-colors hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-400"
                    />
                    <div className="absolute bottom-full left-1/2 z-10 mb-2 hidden w-56 sm:w-64 -translate-x-1/2 transform rounded-lg bg-gray-900 px-3 py-2 text-xs text-white shadow-lg group-hover:block dark:bg-gray-800">
                      <div className="text-center leading-relaxed">
                        Esta nuvem de palavras mostra os alimentos mais
                        consumidos pelo aluno, baseado nas descrições e itens
                        das refeições registradas. Quanto maior a palavra, mais
                        vezes ela foi mencionada.
                      </div>
                      <div className="absolute top-full left-1/2 h-0 w-0 -translate-x-1/2 transform border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-800"></div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-1 items-center justify-center overflow-hidden p-3 sm:p-4">
                {stats.word_cloud_image_url ? (
                  <img
                    src={stats.word_cloud_image_url}
                    alt="Nuvem de palavras - Alimentos mais consumidos"
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center p-4 sm:p-6">
                    <FiCoffee
                      size={40}
                      className="sm:w-12 sm:h-12 text-gray-400 dark:text-gray-600"
                    />
                    <p className="mt-3 sm:mt-4 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                      Nenhuma palavra encontrada
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {stats.weekday_stats.length > 0 && (
        <PieChart
          title="Calorias por Dia da Semana"
          data={weekdayCaloriesData}
          labels={weekdayLabels}
          colors={weekdayColorsArray}
          tooltipText="Este gráfico mostra a distribuição de calorias consumidas por dia da semana. Cada fatia representa um dia da semana, permitindo identificar em quais dias o aluno consome mais ou menos calorias."
        />
      )}

      {stats.meal_type_stats.length > 0 && (
        <PieChart
          title="Refeições por Tipo"
          data={mealTypeData}
          labels={mealTypeLabels}
          colors={mealTypeColorsArray}
          tooltipText="Este gráfico mostra a distribuição de refeições registradas por tipo (café da manhã, almoço, jantar, etc.). Cada fatia representa um tipo de refeição, permitindo visualizar quais tipos de refeição são mais registrados pelo aluno."
        />
      )}
    </div>
  );
}
